import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { siteConfig } from '../../config/site';

const Wrap = styled.section`
  background:
    radial-gradient(900px 500px at 20% -10%, rgba(0,153,0,0.20), transparent 55%),
    radial-gradient(900px 500px at 80% 110%, rgba(0,153,0,0.15), transparent 55%),
    linear-gradient(180deg, var(--background-dark), var(--background-light));
  padding: 6rem 1rem 4rem;
  position: relative;
  overflow: hidden;
`;

const Inner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1.1fr 1fr;
  gap: 3rem;
  align-items: center;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    text-align: center;
  }
`;

const Copy = styled.div` color: var(--text-color); `;

const Eyebrow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.45rem 1rem;
  border-radius: 999px;
  border: 1px solid rgba(0,153,0,0.5);
  background: rgba(0,153,0,0.12);
  color: var(--primary-color);
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 1.25rem;
  .dot { width: 8px; height: 8px; border-radius: 50%; background: var(--primary-color); box-shadow: 0 0 12px var(--primary-color); }
`;

const Title = styled.h1`
  font-size: clamp(2.5rem, 5.5vw, 4.5rem);
  line-height: 1.03;
  margin: 0 0 1.25rem;
  span { color: var(--primary-color); }
`;

const Lede = styled.p`
  font-size: 1.15rem;
  line-height: 1.75;
  color: var(--text-secondary);
  margin: 0 0 2rem;
  max-width: 620px;
  @media (max-width: 900px) { margin-left: auto; margin-right: auto; }
`;

const Actions = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 2rem;
  @media (max-width: 900px) { justify-content: center; }
`;

const PrimaryLink = styled.a`
  background: var(--primary-color);
  color: var(--text-color);
  padding: 1rem 2rem;
  border-radius: var(--border-radius);
  font-weight: 800;
  text-decoration: none;
  transition: transform 0.2s ease;
  &:hover { transform: translateY(-2px); }
`;

const GhostLink = styled(Link)`
  border: 1px solid var(--primary-color);
  color: var(--primary-color);
  padding: 1rem 2rem;
  border-radius: var(--border-radius);
  font-weight: 700;
  text-decoration: none;
  transition: background 0.2s ease;
  &:hover { background: rgba(0, 153, 0, 0.1); }
`;

const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  margin-top: 2rem;

  @media (max-width: 700px) { grid-template-columns: repeat(2, 1fr); }

  .stat {
    text-align: left;
    padding: 0.75rem 1rem;
    border-radius: 12px;
    border: 1px solid rgba(255,255,255,0.08);
    background: rgba(255,255,255,0.03);
  }
  strong { display: block; font-size: 1.5rem; font-weight: 800; color: var(--primary-color); line-height: 1.1; }
  span { font-size: 0.8rem; color: var(--text-secondary); }
`;

const Right = styled.div` position: relative; `;

const Media = styled.figure`
  margin: 0;
  aspect-ratio: 4/3;
  border-radius: 1.75rem;
  overflow: hidden;
  border: 1px solid rgba(0,153,0,0.35);
  background: rgba(0,0,0,0.4);
  box-shadow: 0 30px 80px -30px rgba(0,0,0,0.6);
  img { width: 100%; height: 100%; object-fit: cover; display: block; }
`;

const Float = styled.div`
  position: absolute;
  z-index: 2;
  background: rgba(4,12,7,0.9);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(0,153,0,0.35);
  border-radius: 1rem;
  padding: 0.85rem 1.15rem;
  color: var(--text-color);
  display: flex; gap: 0.6rem; align-items: center;
  box-shadow: 0 15px 40px -15px rgba(0,0,0,0.7);
  min-width: 200px;

  &.tl { top: 1rem; left: -1rem; }
  &.br { bottom: 1rem; right: -1rem; }

  @media (max-width: 900px) { display: none; }

  .icon {
    width: 34px; height: 34px;
    border-radius: 10px;
    background: rgba(0,153,0,0.15); color: var(--primary-color);
    display: inline-flex; align-items: center; justify-content: center; font-weight: 800;
  }
  strong { display: block; font-size: 0.9rem; }
  small { color: var(--text-secondary); font-size: 0.75rem; }
`;

const ShopMegaHero: React.FC = () => (
  <Wrap>
    <Inner>
      <Copy>
        <Eyebrow><span className="dot" /> Trusted flash USDT software shop</Eyebrow>
        <Title>
          Buy licensed <span>flash USDT software</span> — TRC-20, ERC-20 &amp; BEP-20 in minutes.
        </Title>
        <Lede>
          {siteConfig.brand.name} is the online store for professional flash USDT software.
          Pay in BTC or USDT, receive your license key by email within minutes, and start
          creating realistic USDT balances on 50+ wallets across all three major networks.
        </Lede>
        <Actions>
          <PrimaryLink href={`https://wa.me/${siteConfig.contact.whatsapp}`} target="_blank" rel="noopener noreferrer">
            Buy on WhatsApp
          </PrimaryLink>
          <GhostLink to="/pricing">Shop pricing plans</GhostLink>
        </Actions>
        <Stats>
          <div className="stat"><strong>$10M</strong><span>Daily flash cap</span></div>
          <div className="stat"><strong>365d</strong><span>Flash validity</span></div>
          <div className="stat"><strong>50+</strong><span>Wallets</span></div>
          <div className="stat"><strong>24/7</strong><span>WhatsApp support</span></div>
        </Stats>
      </Copy>
      <Right>
        <Media>
          <img src="/img/hero-shop.jpg" alt={`${siteConfig.brand.name} — flash USDT software preview across TRC-20, ERC-20 and BEP-20`} />
        </Media>
        <Float className="tl">
          <span className="icon">$</span>
          <div><strong>Instant delivery</strong><small>License by email in minutes</small></div>
        </Float>
        <Float className="br">
          <span className="icon">✓</span>
          <div><strong>Multi-chain</strong><small>TRC-20 · ERC-20 · BEP-20</small></div>
        </Float>
      </Right>
    </Inner>
  </Wrap>
);

export default ShopMegaHero;
